import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, Loader2, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config";

const EASE = [0.16, 1, 0.3, 1];

const QUESTIONS = [
  {
    q: "Qual a sua faixa etária?",
    options: [
      "Menor de 18 anos",
      "18 a 20 anos",
      "21 a 24 anos",
      "25 a 29 anos",
      "30 anos ou mais",
    ],
  },
  {
    q: "Há quanto tempo você treina musculação?",
    options: [
      "6 meses há 1 ano de treino",
      "1 a 2 anos de treino",
      "2 a 3 anos de treino",
      "3 a 5 anos de treino",
      "Mais de 5 anos de treino",
    ],
  },
  {
    q: "Como você se sente em relação ao seu físico hoje?",
    options: [
      "Satisfeito com os resultados que obtive",
      "Um pouco insatisfeito. Está bom, mas poderia estar melhor",
      "Frustrado com os resultados. Sinto que deveria estar melhor pelo esforço que faço.",
      "Muito frustrado / travado / estagnado",
    ],
  },
  {
    q: "O que mais te motivou a tomar a decisão de buscar ajuda profissional?",
    options: [
      "Quero melhorar aos poucos os meus resultados",
      "Quero entender melhor o processo e aprender com vocês",
      "Quero acelerar meus resultados",
      "Cansado de tentar sozinho e quero resolver isso de vez",
    ],
  },
  {
    q: "Qual o seu principal objetivo hoje?",
    options: [
      "Perder gordura",
      "Ganhar massa muscular",
      "Perder gordura e ganhar massa muscular",
      "Competir no fisiculturismo",
    ],
  },
  {
    q: "Quais suplementos você utiliza?",
    multi: true,
    note: "É possível marcar várias opções",
    options: [
      "Creatina",
      "Whey Protein",
      "Cafeína",
      "Beta-alanina",
      "Multivitamínico",
      "Pré-treino",
      "Não uso suplemento algum",
    ],
  },
  {
    q: "Você já teve acompanhamento com treinador ou nutricionista?",
    options: [
      "Nunca segui nada estruturado",
      "Já tentei montar por conta própria com base no meu conhecimento",
      "Já segui treinos ou dietas da internet (YouTube, Instagram, etc)",
      "Já tive acompanhamento profissional",
      "Já tive mais de um acompanhamento profissional",
    ],
  },
  {
    q: "O que você acredita que está te impedindo de evoluir hoje?",
    options: [
      "Falta de consistência na dieta e/ou treino",
      "Não sei estruturar treino/dieta de forma eficiente",
      "Já tentei algumas estratégias sozinhas, mas não tive os resultados que queria",
      "Não evoluo há bastante tempo, mesmo tentando",
    ],
  },
  {
    q: "Hoje, considerando academia, alimentação e suplementação, quanto você acredita investir em média por mês?",
    note: "não influencia no valor da consultoria, é apenas para entendermos seu contexto",
    options: [
      "Menos de R$200,00",
      "R$200 - R$400,00",
      "R$400 - R$600,00",
      "R$600 - R$900,00",
      "Mais de R$900,00",
    ],
  },
  {
    q: "Em qual momento você se encontra hoje em relação a buscar ajuda profissional para evoluir?",
    options: [
      "Ainda estou pesquisando e entendendo melhor meu caso",
      "Tenho interesse, mas não sei se agora é o momento",
      "Quero resolver isso em breve, estou avaliando opções",
      "Quero começar o quanto antes!",
    ],
  },
  {
    q: "Olhando para o seu momento atual, o que mais pesou na sua decisão de buscar ajuda agora?",
    options: [
      "Gostaria de melhorar a aparência atual",
      "Não vê o resultado que eu esperava pelo esforço",
      "Sente que poderia estar melhor pelo tempo que já treina",
      "Não sabe exatamente o que está fazendo e não quer mais ter essa preocupação",
    ],
  },
];

const LETTERS = ["A", "B", "C", "D", "E", "F", "G"];
const TOTAL_STEPS = QUESTIONS.length + 1; // perguntas + passo de contato
const Q_MOTIVACAO = 3;

export default function StartFormModal({ open, onClose }) {
  const [step, setStep] = useState(0); // 0=intro, 1..N=perguntas, N+1=contato
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);

  const reset = () => {
    setTimeout(() => {
      setStep(0);
      setAnswers(Array(QUESTIONS.length).fill(null));
      setName("");
      setPhone("");
    }, 400);
  };

  const close = () => {
    onClose();
    reset();
  };

  const answered = (i) => {
    const a = answers[i];
    return Array.isArray(a) ? a.length > 0 : a !== null;
  };

  const pick = (optIdx) => {
    const next = [...answers];
    const q = QUESTIONS[step - 1];
    if (q.multi) {
      const arr = Array.isArray(next[step - 1]) ? [...next[step - 1]] : [];
      const pos = arr.indexOf(optIdx);
      if (pos >= 0) arr.splice(pos, 1);
      else arr.push(optIdx);
      next[step - 1] = arr;
    } else {
      next[step - 1] = optIdx;
    }
    setAnswers(next);
  };

  const ok = () => {
    if (!answered(step - 1)) return;
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };

  const back = () => {
    if (step === TOTAL_STEPS && name === "" && phone === "") {
      setStep(QUESTIONS.length);
    } else {
      setStep((s) => Math.max(s - 1, 0));
    }
  };

  const answerText = (i) => {
    const a = answers[i];
    return Array.isArray(a)
      ? a.map((x) => QUESTIONS[i].options[x]).join(", ")
      : QUESTIONS[i].options[a];
  };

  const submit = async () => {
    if (sending) return;
    if (name.trim().length < 2) {
      toast.error("Digite o seu nome.");
      return;
    }
    if (phone.trim().length < 8) {
      toast.error("Digite o seu WhatsApp com DDD.");
      return;
    }
    setSending(true);
    try {
      const resumo = QUESTIONS.map(
        (q, i) => `${i + 1}. ${q.q} — ${answerText(i)}`,
      ).join("\n");
      const motivacao = QUESTIONS[Q_MOTIVACAO].options[answers[Q_MOTIVACAO]];
      await axios.post(`${API}/contact`, {
        name: name.trim(),
        phone: phone.trim(),
        goal: `Quiz — ${motivacao}`.slice(0, 80),
        message: resumo,
      });
      toast.success(
        "Respostas enviadas! Lucas vai te chamar no WhatsApp em até 24h.",
      );
      close();
    } catch {
      toast.error("Não foi possível enviar. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  const progress = Math.max(step, 0) / TOTAL_STEPS;
  const question = step >= 1 && step <= QUESTIONS.length ? QUESTIONS[step - 1] : null;
  const canOk = step >= 1 && step <= QUESTIONS.length && answered(step - 1);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: EASE }}
          data-testid="start-form-modal"
          className="fixed inset-0 z-[100] overflow-y-auto bg-black"
        >
          {/* Barra de progresso */}
          {step > 0 && (
            <div className="fixed inset-x-0 top-0 z-10 h-1.5 bg-white/15">
              <motion.div
                className="h-full bg-white"
                initial={false}
                animate={{ width: `${progress * 100}%` }}
                transition={{ duration: 0.5, ease: EASE }}
              />
            </div>
          )}

          <button
            data-testid="start-form-close"
            onClick={close}
            aria-label="Fechar"
            className="fixed right-5 top-5 z-10 grid h-11 w-11 place-items-center border border-white/20 text-white transition-colors duration-300 hover:bg-white hover:text-black"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex min-h-screen flex-col">
            <div className="flex flex-1 items-center justify-center px-6 pb-8 pt-16">
              <div className="w-full max-w-xl">
                <AnimatePresence mode="wait">
                  {/* INTRO */}
                  {step === 0 && (
                    <motion.div
                      key="intro"
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -16 }}
                      transition={{ duration: 0.35, ease: EASE }}
                      className="text-center"
                    >
                      <p className="text-xl font-bold leading-relaxed text-white md:text-2xl md:leading-relaxed">
                        Em poucos minutos, você vai responder algumas perguntas
                        para entendermos melhor seu momento atual e como
                        podemos te ajudar.
                      </p>
                      <p className="mt-6 text-lg leading-relaxed text-white/75 md:text-xl md:leading-relaxed">
                        Levamos nosso trabalho a{" "}
                        <strong className="text-white">sério</strong> — por
                        isso, já queremos conhecer você desde o início para,
                        juntos,{" "}
                        <strong className="text-white">
                          traçarmos um plano estratégico
                        </strong>{" "}
                        de evolução.
                      </p>
                      <p className="mt-6 text-lg leading-relaxed text-white/75 md:text-xl md:leading-relaxed">
                        <strong className="text-white">É rápido</strong>, mas
                        precisa ser feito com{" "}
                        <strong className="text-white">atenção</strong>.
                      </p>
                    </motion.div>
                  )}

                  {/* PERGUNTA */}
                  {question && (
                    <motion.div
                      key={`q-${step}`}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -24 }}
                      transition={{ duration: 0.3, ease: EASE }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-2xl font-bold leading-snug text-white md:text-3xl md:leading-snug">
                            {question.q}
                          </h3>
                          {question.note && (
                            <p className="mt-2 text-sm italic leading-snug text-white/55 md:text-base">
                              {question.note}
                            </p>
                          )}
                        </div>
                        <span className="mt-1 font-mono text-xl text-white/70">
                          *
                        </span>
                      </div>
                      <div className="mt-8 flex flex-col gap-3">
                        {question.options.map((opt, i) => {
                          const a = answers[step - 1];
                          const selected = question.multi
                            ? Array.isArray(a) && a.includes(i)
                            : a === i;
                          return (
                            <button
                              key={i}
                              data-testid={`quiz-option-${step - 1}-${i}`}
                              onClick={() => pick(i)}
                              className={`flex items-center gap-4 border p-4 text-left transition-all duration-200 ${
                                selected
                                  ? "border-white bg-white/10"
                                  : "border-white/20 hover:border-white/50"
                              }`}
                            >
                              <span
                                className={`grid h-9 w-9 shrink-0 place-items-center border font-mono text-sm ${
                                  selected
                                    ? "border-white bg-white text-black"
                                    : "border-white/30 text-white/70"
                                }`}
                              >
                                {LETTERS[i]}
                              </span>
                              <span className="text-base leading-snug text-white/85 md:text-lg">
                                {opt}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}

                  {/* CONTATO */}
                  {step === TOTAL_STEPS && (
                    <motion.div
                      key="contato"
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -24 }}
                      transition={{ duration: 0.3, ease: EASE }}
                    >
                      <h3 className="text-2xl font-bold leading-snug text-white md:text-3xl md:leading-snug">
                        Último passo.
                      </h3>
                      <p className="mt-3 text-base leading-relaxed text-white/65 md:text-lg">
                        Deixe seu nome e WhatsApp para eu te responder
                        pessoalmente.
                      </p>
                      <div className="mt-8 flex flex-col gap-4">
                        <div>
                          <label
                            htmlFor="quiz-name"
                            className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50"
                          >
                            Seu nome
                          </label>
                          <input
                            id="quiz-name"
                            data-testid="quiz-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Como você se chama?"
                            className="h-12 w-full border border-white/20 bg-white/5 px-4 text-white placeholder:text-white/30 focus:border-white focus:outline-none"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="quiz-phone"
                            className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50"
                          >
                            Seu WhatsApp
                          </label>
                          <input
                            id="quiz-phone"
                            data-testid="quiz-phone"
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="(47) 99999-9999"
                            className="h-12 w-full border border-white/20 bg-white/5 px-4 text-white placeholder:text-white/30 focus:border-white focus:outline-none"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Rodapé de navegação */}
            <div className="px-6 pb-8 md:px-10">
              {step === 0 && (
                <button
                  data-testid="start-form-begin"
                  onClick={() => setStep(1)}
                  className="btn-primary mx-auto w-full max-w-xl text-xl"
                >
                  Começar
                </button>
              )}

              {step > 0 && step <= QUESTIONS.length && (
                <div className="mx-auto flex w-full max-w-xl gap-3">
                  <button
                    data-testid="quiz-back"
                    onClick={back}
                    aria-label="Voltar"
                    className="btn-primary !px-0 w-16 shrink-0"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    data-testid="quiz-ok"
                    onClick={ok}
                    disabled={!canOk}
                    className="btn-primary w-full text-xl disabled:pointer-events-none disabled:opacity-40"
                  >
                    OK
                  </button>
                </div>
              )}

              {step === TOTAL_STEPS && (
                <div className="mx-auto flex w-full max-w-xl gap-3">
                  <button
                    data-testid="quiz-back"
                    onClick={back}
                    aria-label="Voltar"
                    className="btn-primary !px-0 w-16 shrink-0"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    data-testid="quiz-submit"
                    onClick={submit}
                    disabled={sending}
                    className="btn-primary w-full text-xl disabled:opacity-60"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      "Enviar minhas respostas"
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
